import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaBuilding, FaEye, FaDownload } from "react-icons/fa";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import styles from "../../master/academicConfiguration/Academiclist.module.css";
import { CustomButton, CustomDropdown } from "../../../../components";
import CustomMRTTable from "../../../../components/CustomMRTTable/CustomMRTTable";
import AxiosInstance, { TokenService } from "../../../../services/Api";

const GroupFeeList = () => {
  const navigate = useNavigate();
  const { translations } = useSelector((state) => state.language);
  const labels = translations?.labels || {};

  const user = TokenService.getUser();
  const clientData = user?.client_data?.[0] || {};
  const school_id = clientData?.school_id || "";
  // Fallback academic year id, used only when the user hasn't picked one in the dropdown.
  const tokenAcademicId = clientData?.academic_year_id || "";

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [apiSearch, setApiSearch] = useState("");
  const [rowCount, setRowCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });

  // ── Academic Year filter dropdown (from API) ──
  const [academicYear, setAcademicYear] = useState("");
  const [academicYearOptions, setAcademicYearOptions] = useState([]);
  const [academicYearLoading, setAcademicYearLoading] = useState(false);
  // Gate the list fetch until the academic years are loaded and the default
  // active year is resolved — prevents the list API from firing twice on mount.
  const [academicYearResolved, setAcademicYearResolved] = useState(false);
  const academicIdToSend = academicYear || tokenAcademicId;

  useEffect(() => {
    fetchAcademicYears();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [school_id]);

  /** Academic Year list API: GET school/academic/{school_id} */
  const fetchAcademicYears = async () => {
    if (!school_id) return;
    setAcademicYearLoading(true);
    try {
      const response = await AxiosInstance.get(`/school/academic/${school_id}`);

      if (response?.data?.success && Array.isArray(response.data.data)) {
        const list = response.data.data;
        const options = list
          .filter((item) => item.isActive === 1 || item.isUpcoming === 1)
          .map((item) => ({
            value: String(item.id),
            label: item.academic_year,
          }));
        setAcademicYearOptions(options);

        // Default-select the active academic year (isActive === 1) if the
        // user hasn't already picked one.
        const activeYear = list.find((item) => item.isActive === 1);
        if (activeYear) {
          setAcademicYear((prev) => prev || String(activeYear.id));
        }
      } else {
        setAcademicYearOptions([]);
      }
    } catch (error) {
      console.error("Error fetching academic years:", error);
      setAcademicYearOptions([]);
    } finally {
      setAcademicYearLoading(false);
      setAcademicYearResolved(true);
    }
  };

  const handleAcademicYearChange = (value) => {
    const finalValue =
      value && typeof value === "object" ? (value.value ?? "") : value;
    setAcademicYear(finalValue || "");
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  // ---- Single place that maps an API row -> table row --------------------
  const transformRow = (item) => ({
    id: item.id,
    class_name: item.class_name || "",
    section_name: item.section || "",
    group_name: item.group_name || "",
    demand_date: item.demand_date
      ? dayjs(item.demand_date).format("DD/MM/YYYY")
      : "-",
    due_date: item.due_date ? dayjs(item.due_date).format("DD/MM/YYYY") : "-",
  });

  // ---- Fetch (server-side pagination + search) ---------------------------
  useEffect(() => {
    if (school_id && academicYearResolved) fetchGroupFeeList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    school_id,
    academicYearResolved,
    pagination.pageIndex,
    pagination.pageSize,
    apiSearch,
    academicIdToSend,
  ]);

  const fetchGroupFeeList = async () => {
    setLoading(true);
    try {
      const payload = {
        school_id,
        academic_year_id: Number(academicIdToSend) || null,
        search: apiSearch || "",
        class_id: "",
        section_id: "",
        page: pagination.pageIndex + 1,
        limit: pagination.pageSize,
      };

      const response = await AxiosInstance.post(
        "schoolFees/feeGroup/GroupList",
        payload,
      );

      if (response?.data?.status) {
        const list = response.data.data || [];
        setData(list.map(transformRow));
        setRowCount(response.data.pagination?.total ?? list.length);
      } else {
        setData([]);
        setRowCount(0);
      }
    } catch (error) {
      console.error("Error fetching group fee list:", error);
      setData([]);
      setRowCount(0);
    } finally {
      setLoading(false);
    }
  };

  // ---- Navigation --------------------------------------------------------
  const handleAdd = () => {
    navigate("/user/createGroupFee");
  };

  const handleView = (row) => {
    navigate("/user/createGroupFee", {
      state: { ID: row.id, academicYearId: academicYear || null },
    });
  };

  // ---- CSV Download ------------------------------------------------------
  const escapeCSV = (field) => {
    const s = String(field ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  const convertToCSV = (rows) => {
    if (!rows?.length) return "";
    const headers = [
      labels.serialNo || "S.No",
      labels.classLabel || "Class",
      labels.sectionLabel || "Section",
      labels.groupName || "Group Name",
      labels.demandDate || "Demand Date",
      labels.dueDate || "Due Date",
    ];
    const body = rows.map((r, i) =>
      [
        i + 1,
        r.class_name,
        r.section_name,
        r.group_name,
        r.demand_date,
        r.due_date,
      ]
        .map(escapeCSV)
        .join(","),
    );
    return [headers.join(","), ...body].join("\n");
  };

  const downloadCSV = (csvContent, fileName) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleDownload = async () => {
    setDownloadLoading(true);
    try {
      // NOTE: passing empty page/limit makes the API return the full record
      // set in one response, so this must NOT loop over pages — the API's
      // totalPages is unreliable when page/limit are blank and looping
      // caused the same rows to be appended more than once.
      const response = await AxiosInstance.post(
        "schoolFees/feeGroup/GroupList",
        {
          school_id,
          academic_year_id: Number(academicIdToSend) || null,
          search: apiSearch || "",
          class_id: "",
          section_id: "",
          page: "",
          limit: "",
        },
      );

      const allData = response?.data?.status ? response.data.data || [] : [];

      const rows = allData.map(transformRow);
      downloadCSV(
        convertToCSV(rows),
        `group-fee-${dayjs().format("YYYY-MM-DD")}.csv`,
      );
    } catch (error) {
      console.error("Error downloading group fee list:", error);
    } finally {
      setDownloadLoading(false);
    }
  };

  // ---- Columns (matches the screenshot) ----------------------------------
  const columns = useMemo(
    () => [
      {
        id: "serialNo",
        header: labels.serialNo || "S.No",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) =>
          pagination.pageIndex * pagination.pageSize + row.index + 1,
      },
      {
        accessorKey: "class_name",
        header: labels.classLabel || "Class",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "section_name",
        header: labels.sectionLabel || "Section",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "group_name",
        header: labels.groupName || "Group Name",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "demand_date",
        header: labels.demandDate || "Demand Date",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        accessorKey: "due_date",
        header: labels.dueDate || "Due Date",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        id: "actions",
        header: labels.actions || "actions",
        enableSorting: false,
        enableColumnFilter: false,
        Cell: ({ row }) => (
          <button
            className={styles.viewBtn}
            onClick={() => handleView(row.original)}
            title={labels.view || "View"}
          >
            <FaEye />
          </button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [labels, pagination.pageIndex, pagination.pageSize, academicYear],
  );

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerIcon}>
          <FaBuilding />
        </div>
        <h1 className={styles.headerTitle}>{labels.groupFee || "Group Fee"}</h1>
      </div>

      {/* Filters + Actions */}
      <div className={styles.actionsWithFilter}>
        <div className={styles.academicYearFilter}>
          <CustomDropdown
            label={labels.academicYear || "Academic Year"}
            name="academicYear"
            value={academicYear}
            onChange={handleAcademicYearChange}
            options={academicYearOptions}
            placeholder={
              academicYearLoading
                ? labels.loading || "Loading..."
                : labels.selectAcademicYear || "Select academic year"
            }
            isClearable={false}
            disabled={academicYearLoading}
          />
        </div>

        <div className={styles.actionButtonsGroup}>
          <CustomButton
            type="button"
            variant="contained"
            className={styles.addButton}
            fullWidth={false}
            onClick={handleAdd}
          >
            {labels.add || "ADD"}
          </CustomButton>

          <CustomButton
            type="button"
            variant="contained"
            className={styles.downloadButton}
            fullWidth={false}
            onClick={handleDownload}
            disabled={downloadLoading}
          >
            <FaDownload style={{ marginRight: "8px" }} />
            {downloadLoading
              ? labels.downloading || "Downloading..."
              : labels.download || "Download"}
          </CustomButton>
        </div>
      </div>

      {/* Table */}
      <CustomMRTTable
        columns={columns}
        data={data}
        loading={loading}
        pagination={pagination}
        rowCount={rowCount}
        onPaginationChange={setPagination}
        searchInput={searchInput}
        setSearchInput={setSearchInput}
        enableSearch={true}
        enableFiltering={false}
        manualPagination={true}
        onSearchEnter={(value) => {
          setApiSearch(value);
          setPagination((prev) => ({ ...prev, pageIndex: 0 }));
        }}
      />
    </div>
  );
};

export default GroupFeeList;
